/**
 * PAGE SCROLL — where a screen lands when you arrive on it, and whether the page
 * behind an overlay is allowed to move.
 *
 * NOT A PORT. `design/Games Tonight Mobile.dc.html` was a single-page mock: every
 * screen was a branch of one document, nothing ever unmounted, and the document
 * scroll offset simply carried from screen to screen. The port inherited that,
 * and on a phone it produced three complaints that are all the same missing
 * feature:
 *
 *  1. Discover forgot where you were. Opening a game from row 40 swapped in a
 *     much shorter document, the browser CLAMPED the offset to the new height on
 *     the spot, and coming back landed on whatever survived the clamp. Nothing
 *     had saved the real number, so nothing could restore it — and being deep in
 *     the queue made opening anything expensive, which is exactly backwards for
 *     a screen whose whole job is to be read a long way down.
 *  2. Detail remembered where you were — the same offset seen from the other
 *     side. Leave one detail page at the bottom, open a different game, and you
 *     start at the bottom of a page you have never seen.
 *  3. A sheet did not stop the page. A `position:fixed` overlay does not consume
 *     the gesture, so a scroll over the backdrop (or a flick that started inside
 *     a sheet and ran past its end) moved the screen behind it.
 *
 * The fix is ONE small state machine rather than two modules, because the three
 * are not independent: the lock freezes an offset that a restore may then have
 * to change (What's New and the move sheet both open a game from *inside* an
 * overlay), and the restore must not let the 0 that the lock parks the document
 * at be recorded as the screen's remembered position. Split in two, those two
 * facts become a shared-state problem between them.
 *
 * THE RULE, in one line: every screen keeps its own offset and gets it back;
 * `detail` never does. A tab is a place you RETURN to, so landing at the top of
 * one throws away scrolling the user did. A detail page is PUSHED — you arrive
 * at the top of a game you just chose, the way a native push behaves. `pageKey`
 * is what makes the second half hold between two detail pages as well: the key
 * carries the id, so game B is a different view from game A rather than the same
 * `detail` screen carrying a stale offset.
 *
 * WHY THE LOCK MOVES `<body>` rather than setting `overflow:hidden` on it: iOS
 * Safari does not honour that for touch scrolling, and this is a phone-first
 * app. `position:fixed` + `top:-y` is the technique that holds everywhere, and
 * it has the property this module wants anyway — the parked offset is a number
 * we are holding rather than one we have to read back off a document we have
 * just changed. The gutter padding is the desktop half of it: taking the
 * document out of flow removes the scrollbar, and without compensation the page
 * behind a modal jumps sideways by its width (11px under `.qd-desktop`).
 *
 * TESTABILITY — the `store/history.ts` bargain. The policy (`pageKey`,
 * `remembersScroll`) is pure, and the engine takes an injected `ScrollEnv`, so
 * `scroll.test.ts` runs in the repo's NODE environment against a fake window: no
 * jsdom, no real `window`. Only `browserScrollEnv()` touches a real one.
 *
 * WHAT THIS MODULE DOES NOT DO. It does not touch history — `store/history.ts`
 * owns that, including the `scrollRestoration = 'manual'` that stops the browser
 * restoring an offset of its own against a page it measured before React
 * reacted. The two never talk; a back gesture reaches this module as an ordinary
 * screen change, like a tap on the bottom nav.
 */

import { useLayoutEffect, useRef } from 'react';

import type { GameId, Screen } from './types.ts';

// ---------------------------------------------------------------------------
// which view is on screen — pure
// ---------------------------------------------------------------------------

/**
 * The identity of the scrollable view, which is the screen EXCEPT on `detail`,
 * where two different games are two different views. Only ever compared and used
 * as a map key; nothing parses it.
 */
export function pageKey(screen: Screen, detailId: GameId): string {
  return screen === 'detail' ? 'detail:' + String(detailId) : screen;
}

/** Everything remembers its offset except a pushed detail page — see THE RULE. */
export function remembersScroll(screen: Screen): boolean {
  return screen !== 'detail';
}

// ---------------------------------------------------------------------------
// the injected environment
// ---------------------------------------------------------------------------

/**
 * The five inline properties the lock owns. `document.body.style` satisfies this
 * structurally, and a plain object satisfies it in a test.
 *
 * `width` is deliberately absent: `left:0` + `right:0` already fill the viewport
 * and leave the used width to shrink around `paddingRight`. Adding `width:100%`
 * on top would overflow by the gutter under the default `content-box` sizing and
 * put a horizontal scrollbar on a page that is supposed to be frozen.
 */
export interface LockStyle {
  position: string;
  top: string;
  left: string;
  right: string;
  paddingRight: string;
}

export interface ScrollEnv {
  /** current document scroll offset (`window.scrollY`) */
  y: () => number;
  /** absolute jump (`window.scrollTo(0, y)`) — never smooth; see the note below */
  scrollTo: (y: number) => void;
  /** width the vertical scrollbar occupies right now; 0 with overlay scrollbars */
  gutter: () => number;
  /** `document.body.style` */
  body: LockStyle;
  /** passive document-scroll subscription; returns its teardown */
  onScroll: (fn: () => void) => () => void;
  /** one animation frame; returns a cancel */
  raf: (fn: () => void) => () => void;
  /**
   * The user touching the page — `touchstart`, `wheel`, `keydown`, `pointerdown`
   * — as opposed to the offset merely changing, which a shrinking document
   * does on its own. Only a seeded landing listens; see `landSeeded`.
   */
  onInput: (fn: () => void) => () => void;
}

/** Production wiring. Kept here so the shells stay a one-line hook call. */
export function browserScrollEnv(): ScrollEnv {
  return {
    y: () => window.scrollY,
    // Instant, not smooth — the same call the drag autoscroll and the carousel
    // already make. A restore that animates is a restore the user watches fail.
    scrollTo: (y) => window.scrollTo(0, y),
    gutter: () => Math.max(0, window.innerWidth - document.documentElement.clientWidth),
    body: document.body.style,
    onScroll: (fn) => {
      window.addEventListener('scroll', fn, { passive: true });
      return () => window.removeEventListener('scroll', fn);
    },
    raf: (fn) => {
      const id = requestAnimationFrame(fn);
      return () => cancelAnimationFrame(id);
    },
    onInput: (fn) => {
      const opts = { passive: true } as const;
      const evs = ['touchstart', 'wheel', 'keydown', 'pointerdown'] as const;
      for (const e of evs) window.addEventListener(e, fn, opts);
      return () => {
        for (const e of evs) window.removeEventListener(e, fn);
      };
    },
  };
}

// ---------------------------------------------------------------------------
// the engine
// ---------------------------------------------------------------------------

export interface PageScroll {
  /**
   * Reconcile against the view that has just been committed. Called from a
   * layout effect — after React has swapped the DOM and before the browser
   * paints — because a restore measured against the outgoing screen is the bug
   * this module exists to fix.
   */
  sync: (key: string, remember: boolean, locked: boolean) => void;
  /** Drop the listener, cancel any settle, and RELEASE THE LOCK. */
  stop: () => void;
}

function snapshot(s: LockStyle): LockStyle {
  return { position: s.position, top: s.top, left: s.left, right: s.right, paddingRight: s.paddingRight };
}

function restoreStyle(s: LockStyle, from: LockStyle): void {
  s.position = from.position;
  s.top = from.top;
  s.left = from.left;
  s.right = from.right;
  s.paddingRight = from.paddingRight;
}

/**
 * An offset that survived a reload for a given view, or `null`. Consulted once
 * per commit, BEFORE the in-memory map, and only for a remembering view. The
 * production seed is `store/resume.ts#takeResumeScroll`, which answers exactly
 * once for exactly the key boot restored; the engine keeps ownership of the
 * jump and the one-frame re-assert.
 */
export type ScrollSeed = (key: string) => number | null;

const noSeed: ScrollSeed = () => null;

/**
 * How long a seeded landing keeps re-asserting into a document that is still
 * too short — about three seconds at 60 Hz. See `landSeeded`.
 */
export const SEED_SETTLE_FRAMES = 180;

export function createPageScroll(env: ScrollEnv, seed: ScrollSeed = noSeed): PageScroll {
  /** Remembered offsets, by `pageKey`. Views that never scrolled are absent. */
  const pos = new Map<string, number>();
  /** The committed view. `''` only before the first `sync` — no real key is empty. */
  let key = '';
  let remember = false;

  let locked = false;
  /** The offset the page is parked at while locked, and lands on when released. */
  let lockedY = 0;
  /** The inline `<body>` values the lock overwrote, restored verbatim. */
  let saved: LockStyle | null = null;

  /** Cancels the pending re-assert frame, if there is one. */
  let cancelSettle: (() => void) | null = null;

  const off = env.onScroll(() => {
    // The one offset that is never the user's is the 0 a locked document sits
    // at — the real one is `lockedY`, and recording 0 over it would hand the
    // user the top of the page when the sheet closes.
    if (locked || !remember || key === '') return;
    pos.set(key, env.y());
  });

  /**
   * Land on `target`, then check once on the next frame.
   *
   * The first `scrollTo` is the one that matters and is normally exact: the row
   * count comes from the store and every tile has a fixed `aspect-ratio`, so the
   * screen we are returning to commits at the height it had when we left it. The
   * retry covers the case where it does not — a document still one frame short
   * of the target clamps the jump, and a clamp we do not correct is the original
   * bug wearing a new hat. It is ONE frame on purpose: anything later is content
   * arriving from the network, and yanking the page then would fight a user who
   * has already started reading.
   *
   * A clamped offset IS recorded in the meantime, and that is deliberate. The
   * alternative — suppressing the listener until the frame lands — makes the
   * memory depend on a callback that a background tab never runs, and a screen
   * that has quietly stopped remembering looks exactly like the bug this module
   * fixes. Recording what the page is actually showing and correcting it a frame
   * later cannot get stuck; the worst case is a memory that briefly agrees with
   * the screen.
   */
  const land = (target: number): void => {
    env.scrollTo(target);
    cancelSettle?.();
    cancelSettle = env.raf(() => {
      cancelSettle = null;
      if (!locked && env.y() !== target) env.scrollTo(target);
    });
  };

  /**
   * Land a SEEDED offset — one that survived a reload (`store/resume.ts`).
   *
   * This is the case the one-frame rule above was written to exclude, and it
   * is different for the reason the rule gives: "anything later is content
   * arriving from the network" is exactly what a reload waits on. Discover
   * commits EMPTY on a fresh boot — its server-ranked page is asked for after
   * the landing, and the fallback grid with it — so the jump clamps to 0 and
   * the rows arrive a few hundred milliseconds later under a page that has
   * already given up. Nobody is reading an empty page, so re-asserting into it
   * fights no one.
   *
   * Bounded three ways, each of which hands the page back for good: it lands;
   * the user touches the page (`env.onInput` — a touch, a wheel, a key: an
   * INPUT, not an offset change, because the offset also moves on its own
   * when the document shrinks under it and clamps, which the mobile header
   * does right after commit); or `SEED_SETTLE_FRAMES` have passed. `locked`
   * ends it too — a sheet opened in the meantime owns the document.
   */
  const landSeeded = (target: number): void => {
    env.scrollTo(target);
    cancelSettle?.();
    if (env.y() === target) return;
    let left = SEED_SETTLE_FRAMES;
    let cancelFrame: (() => void) | null = null;
    const offInput = env.onInput(() => cancelSettle?.());
    const done = (): void => {
      cancelFrame?.();
      cancelFrame = null;
      offInput();
      cancelSettle = null;
    };
    const step = (): void => {
      cancelFrame = null;
      if (locked || env.y() === target) return done();
      env.scrollTo(target);
      if (env.y() === target || --left <= 0) return done();
      cancelFrame = env.raf(step);
    };
    cancelFrame = env.raf(step);
    cancelSettle = done;
  };

  const lock = (): void => {
    lockedY = env.y();
    saved = snapshot(env.body);
    // Set BEFORE the style writes: taking the document out of flow drops the
    // offset to 0, and the scroll event that announces it must already be seen
    // as ours.
    locked = true;
    const g = env.gutter();
    env.body.position = 'fixed';
    env.body.top = '-' + lockedY + 'px';
    env.body.left = '0';
    env.body.right = '0';
    if (g > 0) env.body.paddingRight = g + 'px';
  };

  const release = (): void => {
    if (saved) restoreStyle(env.body, saved);
    saved = null;
    locked = false;
    env.scrollTo(lockedY);
    // The scroll event this fires arrives a frame later in a real browser, so the
    // remembered offset is written here rather than left to the listener.
    if (remember && key !== '') pos.set(key, lockedY);
  };

  const sync = (nextKey: string, nextRemember: boolean, nextLocked: boolean): void => {
    // MOUNT ADOPTS, IT DOES NOT MOVE. The first sync is the shell appearing —
    // at boot, or at a breakpoint crossing that unmounted the other tree
    // mid-scroll. Neither is a navigation, and scrolling on either would be this
    // module inventing a jump nobody asked for.
    const mount = key === '';
    const moved = !mount && nextKey !== key;
    key = nextKey;
    remember = nextRemember;

    // THE ONE EXCEPTION TO "MOUNT ADOPTS": an offset that survived a reload for
    // exactly this view (`store/resume.ts`). The map is empty after a reload, so
    // the seed fills the slot the last session's scrolling would have filled —
    // and then the ordinary landing runs. A seed is only ever asked for a
    // remembering view; `detail` stays a push (SC-02) across a reload too.
    const seeded = nextRemember ? seed(nextKey) : null;
    if (seeded !== null) pos.set(nextKey, seeded);

    if (moved || (mount && seeded !== null)) {
      // Absent means "never scrolled here", which lands at the top — the same
      // answer `remember === false` gives, since nothing is ever recorded for it.
      const target = pos.get(nextKey) ?? 0;
      // Navigating from inside an overlay (What's New / the move sheet both open
      // a game): the document is frozen and cannot move, so hand the target to
      // the release instead of scrolling into a `position:fixed` body.
      if (locked) lockedY = target;
      else if (seeded !== null) landSeeded(target);
      else land(target);
    }

    if (nextLocked && !locked) lock();
    else if (!nextLocked && locked) release();
  };

  const stop = (): void => {
    off();
    cancelSettle?.();
    cancelSettle = null;
    // A shell that unmounts while a sheet is open (the breakpoint crossing) must
    // not leave `<body>` fixed — the tree that mounts next takes its own
    // snapshot, and would then "restore" the lock's own values forever.
    if (locked) release();
  };

  return { sync, stop };
}

// ---------------------------------------------------------------------------
// the hook
// ---------------------------------------------------------------------------

/**
 * Mounted once per shell, above the screen switch.
 *
 * `locked` is the caller's, not ours: `store/history.ts#overlayOpen` derives it
 * from the same level list the back gesture unwinds, so a sheet added there is
 * a sheet this locks behind — one list, not two.
 */
export function usePageScroll(
  screen: Screen,
  detailId: GameId,
  locked: boolean,
  seed: ScrollSeed = noSeed,
): void {
  const ref = useRef<PageScroll | null>(null);
  // Read through a ref so a new seed function per render cannot recreate the
  // engine — the engine is created once, on mount.
  const seedRef = useRef(seed);
  seedRef.current = seed;

  useLayoutEffect(() => {
    const ps = createPageScroll(browserScrollEnv(), (k) => seedRef.current(k));
    ref.current = ps;
    return () => {
      ref.current = null;
      ps.stop();
    };
  }, []);

  // Layout, not passive: the restore has to happen between React's DOM swap and
  // the paint, or the user sees one frame of the outgoing screen's offset.
  useLayoutEffect(() => {
    ref.current?.sync(pageKey(screen, detailId), remembersScroll(screen), locked);
  }, [screen, detailId, locked]);
}
